import { createSlice, type PayloadAction } from "@reduxjs/toolkit"
import { logout } from "./auth-slice"
import { type TaskDraft } from "@/features/tasks"

type TaskDraftSlice = {
  drafts: Record<string, TaskDraft>
}

export const initialTaskState: TaskDraft = {
  title: "",
  description: "",
  category: null,
  reminder: null,
  recurrence: null,
  due_date: null,
  is_done: false,
  animals: [],
  notes: [],
  apiaries: [],
  hives: [],
  queens: [],
  parcels: [],
  crop_plantings: [],
  birds_groups: [],
}

const initialState: TaskDraftSlice = {
  drafts: {},
}

const taskDraftSlice = createSlice({
  name: "taskDraft",
  initialState,
  reducers: {
    setTaskDraft: (
      state,
      action: PayloadAction<{ id: string; draft: TaskDraft }>,
    ) => {
      state.drafts[action.payload.id] = action.payload.draft
    },
    setDraftTaskTitle: (
      state,
      action: PayloadAction<{ id: string; title: string }>,
    ) => {
      state.drafts[action.payload.id].title = action.payload.title
    },
    setDraftTaskDescription: (
      state,
      action: PayloadAction<{ id: string; description: string }>,
    ) => {
      state.drafts[action.payload.id].description = action.payload.description
    },
    setDraftTaskCategory: (
      state,
      action: PayloadAction<{ id: string; category: string | null }>,
    ) => {
      state.drafts[action.payload.id].category = action.payload.category
    },
    setDraftTaskReminder: (
      state,
      action: PayloadAction<{ id: string; reminder: number | null }>,
    ) => {
      state.drafts[action.payload.id].reminder = action.payload.reminder
    },
    setDraftTaskRecurrence: (
      state,
      action: PayloadAction<{ id: string; recurrence: number | null }>,
    ) => {
      state.drafts[action.payload.id].recurrence = action.payload.recurrence
    },
    setDraftTaskDueDate: (
      state,
      action: PayloadAction<{ id: string; due_date: string | null }>,
    ) => {
      state.drafts[action.payload.id].due_date = action.payload.due_date
    },
    setDraftTaskIsDone: (
      state,
      action: PayloadAction<{ id: string; is_done: boolean }>,
    ) => {
      state.drafts[action.payload.id].is_done = action.payload.is_done
    },
    setDraftTaskAnimals: (
      state,
      action: PayloadAction<{ id: string; animals: string[] }>,
    ) => {
      state.drafts[action.payload.id].animals = action.payload.animals
    },
    setDraftTaskNotes: (
      state,
      action: PayloadAction<{ id: string; notes: string[] }>,
    ) => {
      state.drafts[action.payload.id].notes = action.payload.notes
    },
    setDraftTaskApiaries: (
      state,
      action: PayloadAction<{ id: string; apiaries: string[] }>,
    ) => {
      state.drafts[action.payload.id].apiaries = action.payload.apiaries
    },
    setDraftTaskHives: (
      state,
      action: PayloadAction<{ id: string; hives: string[] }>,
    ) => {
      state.drafts[action.payload.id].hives = action.payload.hives
    },
    setDraftTaskQueens: (
      state,
      action: PayloadAction<{ id: string; queens: string[] }>,
    ) => {
      state.drafts[action.payload.id].queens = action.payload.queens
    },
    setDraftTaskBirdsGroups: (
      state,
      action: PayloadAction<{ id: string; birds_groups: string[] }>,
    ) => {
      state.drafts[action.payload.id].birds_groups = action.payload.birds_groups
    },
    setDraftTaskParcels: (
      state,
      action: PayloadAction<{ id: string; parcels: string[] }>,
    ) => {
      state.drafts[action.payload.id].parcels = action.payload.parcels
    },
    setDraftTaskCropPlantings: (
      state,
      action: PayloadAction<{ id: string; crop_plantings: string[] }>,
    ) => {
      state.drafts[action.payload.id].crop_plantings =
        action.payload.crop_plantings
    },
    resetTaskDraft: (
      state,
      action: PayloadAction<{ id: string; draft?: Partial<TaskDraft> }>,
    ) => {
      state.drafts[action.payload.id] = {
        ...initialTaskState,
        ...action.payload.draft,
      }
    },
    deleteTaskDraft: (state, action: PayloadAction<string>) => {
      delete state.drafts[action.payload]
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logout.fulfilled, () => initialState)
  },
})

export default taskDraftSlice.reducer

export const {
  setTaskDraft,
  setDraftTaskTitle,
  setDraftTaskDescription,
  setDraftTaskCategory,
  setDraftTaskReminder,
  setDraftTaskRecurrence,
  setDraftTaskDueDate,
  setDraftTaskIsDone,
  setDraftTaskParcels,
  setDraftTaskQueens,
  setDraftTaskApiaries,
  setDraftTaskHives,
  setDraftTaskAnimals,
  setDraftTaskNotes,
  resetTaskDraft,
  deleteTaskDraft,
  setDraftTaskBirdsGroups,
  setDraftTaskCropPlantings,
} = taskDraftSlice.actions
